import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Stack, Button, IconButton, TextField, MenuItem,
  Divider, Alert, Snackbar,
} from '@mui/material';
import { ArrowBack, TaskAlt, Cancel } from '@mui/icons-material';
import { procurementService } from '../../services/procurementService';
import { api } from '../../services/apiClient';
import { formatIndianNumber } from '../../utils/currency';

const n = (v: string) => Number(v) || 0;
const r2 = (x: number) => Math.round(x * 100) / 100;

const SupplierInvoiceNewPage: React.FC = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<any[]>([]);
  const [f, setF] = useState({
    vendorId: '', invoiceNo: '', vendorInvoiceNumber: '', invoiceDate: new Date().toISOString().slice(0, 10),
    dueDate: '', subtotal: '', cgstAmount: '', sgstAmount: '', igstAmount: '', tdsAmount: '', remarks: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { procurementService.getVendors().then((v: any) => setVendors(v.vendors || v || [])).catch(() => {}); }, []);
  const set = (patch: Partial<typeof f>) => setF((s) => ({ ...s, ...patch }));

  const totals = useMemo(() => {
    const gst = r2(n(f.cgstAmount) + n(f.sgstAmount) + n(f.igstAmount));
    const total = r2(n(f.subtotal) + gst);
    const net = r2(total - n(f.tdsAmount));
    return { gst, total, net };
  }, [f]);

  const valid = f.vendorId && f.invoiceNo && n(f.subtotal) > 0;
  const submit = async () => {
    setError(null);
    if (!valid) { setError('Vendor, invoice number and a taxable value are required'); return; }
    setSaving(true);
    try {
      await api.post('/procurement/purchase-invoices', {
        vendorId: f.vendorId, purchaseOrderId: null, invoiceNo: f.invoiceNo, vendorInvoiceNumber: f.vendorInvoiceNumber,
        invoiceDate: f.invoiceDate, dueDate: f.dueDate || null,
        subtotal: n(f.subtotal), cgstAmount: n(f.cgstAmount), sgstAmount: n(f.sgstAmount), igstAmount: n(f.igstAmount),
        totalAmount: totals.total, remarks: f.remarks,
      });
      setToast('Supplier invoice created');
      setTimeout(() => navigate('/procurement/invoices'), 800);
    } catch (e: any) { setError(e.message || 'Failed to create invoice'); } finally { setSaving(false); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate('/procurement/invoices')}><ArrowBack /></IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>New Supplier Invoice</Typography>
            <Typography variant="body2" color="text.secondary">Record a vendor bill with GST &amp; TDS and see its accounting impact.</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<Cancel />} onClick={() => navigate('/procurement/invoices')}>Cancel</Button>
          <Button variant="contained" startIcon={<TaskAlt />} onClick={submit} disabled={saving || !valid}>Create</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Document</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth size="small" label="Supplier" value={f.vendorId} onChange={(e) => set({ vendorId: e.target.value })}>
                  {vendors.map((v) => <MenuItem key={v.id} value={v.id}>{v.company_name || v.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" label="Invoice No" value={f.invoiceNo} onChange={(e) => set({ invoiceNo: e.target.value })} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" label="Vendor Inv. No" value={f.vendorInvoiceNumber} onChange={(e) => set({ vendorInvoiceNumber: e.target.value })} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="date" label="Invoice Date" InputLabelProps={{ shrink: true }} value={f.invoiceDate} onChange={(e) => set({ invoiceDate: e.target.value })} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="date" label="Due Date" InputLabelProps={{ shrink: true }} value={f.dueDate} onChange={(e) => set({ dueDate: e.target.value })} /></Grid>
            </Grid>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>Amounts</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth size="small" type="number" label="Taxable Value" value={f.subtotal} onChange={(e) => set({ subtotal: e.target.value })} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth size="small" type="number" label="IGST" value={f.igstAmount} onChange={(e) => set({ igstAmount: e.target.value })} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth size="small" type="number" label="TDS" value={f.tdsAmount} onChange={(e) => set({ tdsAmount: e.target.value })} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth size="small" type="number" label="CGST" value={f.cgstAmount} onChange={(e) => set({ cgstAmount: e.target.value })} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth size="small" type="number" label="SGST" value={f.sgstAmount} onChange={(e) => set({ sgstAmount: e.target.value })} /></Grid>
            </Grid>
            <TextField fullWidth size="small" label="Remarks" sx={{ mt: 2 }} value={f.remarks} onChange={(e) => set({ remarks: e.target.value })} />
          </CardContent></Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ position: 'sticky', top: 16 }}><CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Totals</Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Row label="Taxable Value" value={formatIndianNumber(n(f.subtotal))} />
              <Row label="GST" value={formatIndianNumber(totals.gst)} />
              <Divider sx={{ my: 0.5 }} />
              <Row label="Invoice Total" value={formatIndianNumber(totals.total)} bold />
              {n(f.tdsAmount) > 0 && <Row label="Less TDS" value={`- ${formatIndianNumber(n(f.tdsAmount))}`} />}
              {n(f.tdsAmount) > 0 && <Row label="Net Payable" value={formatIndianNumber(totals.net)} bold />}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Accounting Preview</Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>Dr Purchases / Inventory {formatIndianNumber(n(f.subtotal))}</Typography>
              {totals.gst > 0 && <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>Dr Input GST {formatIndianNumber(totals.gst)}</Typography>}
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{'  '}Cr Accounts Payable {formatIndianNumber(totals.total)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>TDS is deducted at payment (Cr TDS Payable). Stock lines clear GR-IR at 3-way match.</Typography>
            </Stack>
          </CardContent></Card>
        </Grid>
      </Grid>
      <Snackbar open={!!toast} autoHideDuration={2000} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

const Row: React.FC<{ label: string; value: string; bold?: boolean }> = ({ label, value, bold }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 500 }}>{value}</Typography>
  </Stack>
);

export default SupplierInvoiceNewPage;
